import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const StudyPlanPricing = () => {
  const [durations, setDurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  // Add form state
  const [newPrice, setNewPrice] = useState("");
  const [
  newOriginalPrice,
  setNewOriginalPrice
] = useState("");
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");

  // Edit state: map of id -> edit object
  const [edits, setEdits] = useState({});

  const token = JSON.parse(localStorage.getItem("user"))?.token;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchDurations();
  }, []);

  const fetchDurations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/study-plan-prices`, { headers });
      setDurations(Array.isArray(res.data) ? res.data : []);

      const init = {};
      (res.data || []).forEach((d) => {
        init[d._id] = {
          price: d.price ?? "",
          originalPrice: d.originalPrice ?? "",
          durationInDays: d.durationInDays ?? "",
          startDate: formatDateInput(d.startDate),
          endDate: formatDateInput(d.endDate),
          editing: false,
        };
      });
      setEdits(init);
    } catch (err) {
      console.error("Failed to fetch plan prices:", err);
      alert(err.response?.data?.message || "Failed to load plan prices");
    } finally {
      setLoading(false);
    }
  };

  const formatDateInput = (d) => {
  if (!d) return "";

  try {
    const date = new Date(d);

    if (isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch {
    return "";
  }
};

const calculateDurationDays = (start, end) => {
  if (!start || !end) return "";

  const s = new Date(start);
  const e = new Date(end);

  const diff =
    Math.ceil(
      (e - s) /
      (1000 * 60 * 60 * 24)
    );

  return diff > 0 ? diff : "";
};

const calculateDurationLabel = (days) => {
  if (!days) return "";

  const months = Math.round(days / 30);

  if (months >= 12) {
    const years = Math.round(months / 12);

    return `${years} Year${
      years > 1 ? "s" : ""
    }`;
  }

  return `${months} Month${
    months > 1 ? "s" : ""
  }`;
};

  const handleAdd = async () => {
    if (
 !newPrice ||
 !newStartDate ||
 !newEndDate
) {
      return alert("Please provide duration label, price and duration in days.");
    }

    const durationInDays =
  calculateDurationDays(
    newStartDate,
    newEndDate
  );

const durationLabel =
  calculateDurationLabel(
    durationInDays
  );

const payload = {
durationLabel,

price:Number(newPrice),

originalPrice:
newOriginalPrice
? Number(newOriginalPrice)
: null,

durationInDays,

startDate:newStartDate,

endDate:newEndDate,
};
    if (
  newStartDate &&
  newEndDate &&
  new Date(newEndDate) < new Date(newStartDate)
) {
  return alert("End date cannot be before start date.");
}

if (newStartDate) payload.startDate = newStartDate;
if (newEndDate) payload.endDate = newEndDate;

    setAdding(true);
    try {
      await axios.post(`${API_BASE}/api/admin/study-plan-prices`, payload, { headers });      
      setNewPrice("");        
      setNewOriginalPrice("");    
      setNewStartDate("");
      setNewEndDate("");
      await fetchDurations();
      alert("Added successfully");
    } catch (err) {
      console.error("Add failed:", err);
      alert(err.response?.data?.message || "Failed to add plan price");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this plan price?")) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/study-plan-prices/${id}`, { headers });
      await fetchDurations();
    } catch (err) {
      console.error("Delete failed:", err);
      alert(err.response?.data?.message || "Delete failed");
    }
  };

  const handleEditChange = (id, field, value) => {
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const handleSaveEdit = async (id) => {
    const edit = edits[id];
    if (!edit) return;
    if (edit.price === "" || edit.durationInDays === "") {
      return alert("Price and duration (days) are required");
    }
    if (
  edit.startDate &&
  edit.endDate &&
  new Date(edit.endDate) < new Date(edit.startDate)
) {
  return alert("End date cannot be before start date.");
}

    const payload = {
  price: Number(edit.price),

  originalPrice:
    edit.originalPrice
      ? Number(edit.originalPrice)
      : null,

  durationInDays: Number(edit.durationInDays),

  startDate: edit.startDate || undefined,
  endDate: edit.endDate || undefined,
};

    try {
      await axios.put(`${API_BASE}/api/admin/study-plan-prices/${id}`, payload, { headers });
      await fetchDurations();
      alert("Updated successfully");
    } catch (err) {
      console.error("Update failed:", err);
      alert(err.response?.data?.message || "Update failed");
    }
  };

  const cancelEditing = (id, original) => {
    setEdits((prev) => ({
      ...prev,
      [id]: {
        price: original.price ?? "",
        durationInDays: original.durationInDays ?? "",
        startDate: formatDateInput(original.startDate),
        endDate: formatDateInput(original.endDate),
        editing: false,
      },
    }));
  };

  return (
    <div className="p-6 max-w-5xl mx-auto text-gray-900 dark:text-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">📋 Study Plan Pricing (Admin)</h2>
        <Link to="/admin/plans/manage">
          <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600">
            Upload Study Plans
          </button>
        </Link>
      </div>

      {/* Add new plan */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded shadow mb-6 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
        {[
  {
    label: "Original Price (₹)",
    value: newOriginalPrice,
    set: setNewOriginalPrice,
    type: "number",
  },

  {
    label: "Discounted Price (₹)",
    value: newPrice,
    set: setNewPrice,
    type: "number",
  },

  {
    label: "Start Date",
    value: newStartDate,
    set: setNewStartDate,
    type: "date",
  },

  {
    label: "End Date",
    value: newEndDate,
    set: setNewEndDate,
    type: "date",
  },
]
.map((field, i) => (
          <div key={i}>
            <label className="block text-sm font-medium">{field.label}</label>
            <input
  type={field.type || "text"}
  min={
    field.label === "End Date (optional)"
      ? newStartDate || undefined
      : undefined
  }
  className="border p-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
  placeholder={field.placeholder || ""}
  value={field.value}
  onChange={(e) => field.set(e.target.value)}
/>
          </div>
        ))}
        <div>
          <button
            onClick={handleAdd}
            disabled={adding}
            className="bg-green-600 text-white px-4 py-2 rounded w-full hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600"
          >
            {adding ? "Adding..." : "Add Price"}
          </button>
        </div>
      </div>

      {/* List of durations */}
      <div className="bg-white dark:bg-gray-800 rounded shadow overflow-hidden">
        <div className="grid grid-cols-6 gap-2 bg-gray-100 dark:bg-gray-700 p-3 font-semibold text-sm">
          <div>Label</div>
          <div>Price</div>
          <div>Duration (days)</div>
          <div>Start Date</div>
          <div>End Date</div>
          <div className="text-right">Actions</div>
        </div>

        {loading && <div className="p-4 text-center text-gray-600 dark:text-gray-400">Loading...</div>}

        {!loading && durations.length === 0 && (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">No prices defined.</div>
        )}

        {durations.map((item) => {
          const e = edits[item._id] || {
  price: item.price ?? "",
  originalPrice: item.originalPrice ?? "",
  durationInDays: item.durationInDays ?? "",
  startDate: formatDateInput(item.startDate),
  endDate: formatDateInput(item.endDate),
  editing: false,
};

          return (
            <div
              key={item._id}
              className="grid grid-cols-6 gap-2 p-3 items-center border-t dark:border-gray-700"
            >
              <div className="truncate font-medium">{item.durationLabel}</div>
              <div>
                {e.editing ? (
  <div className="space-y-1">
    <input
      type="number"
      placeholder="Original Price"
      className="border p-1 rounded w-full dark:bg-gray-700 dark:border-gray-600"
      value={e.originalPrice || ""}
      onChange={(ev) =>
        handleEditChange(
          item._id,
          "originalPrice",
          ev.target.value
        )
      }
    />

    <input
      type="number"
      placeholder="Discounted Price"
      className="border p-1 rounded w-full dark:bg-gray-700 dark:border-gray-600"
      value={e.price}
      onChange={(ev) =>
        handleEditChange(
          item._id,
          "price",
          ev.target.value
        )
      }
    />
  </div>
) : (
                  <div>

{item.originalPrice && (
<span className="
line-through
text-gray-400
mr-2
">
₹{item.originalPrice}
</span>
)}

<span className="
text-green-600
font-bold
">
₹{item.price}
</span>

{item.originalPrice && (
<span className="
ml-2
text-xs
bg-red-100
text-red-600
px-2
rounded
">
{Math.round(
(
(item.originalPrice-item.price)
/
item.originalPrice
)*100
)}
% OFF
</span>
)}

</div>
                )}
              </div>
              <div>
                {e.editing ? (
                  <input
                    type="number"
                    className="border p-1 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    value={e.durationInDays}
                    onChange={(ev) => handleEditChange(item._id, "durationInDays", ev.target.value)}
                  />
                ) : (
                  <div>{item.durationInDays} days</div>
                )}
              </div>
              <div>
                {e.editing ? (
                  <input
  type="date"
  className="border p-1 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
  value={e.startDate}
  onChange={(ev) =>
    handleEditChange(item._id, "startDate", ev.target.value)
  }
/>
                ) : (
                  <div className="text-sm text-gray-600 dark:text-gray-400">{formatDateInput(item.startDate) || "—"}</div>
                )}
              </div>
              <div>
                {e.editing ? (
                  <input
  type="date"
  value={e.endDate}
  min={e.startDate || undefined}
  className="border p-1 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
  onChange={(ev) =>
    handleEditChange(item._id, "endDate", ev.target.value)
  }
/>
                ) : (
                  <div className="text-sm text-gray-600 dark:text-gray-400">{formatDateInput(item.endDate) || "—"}</div>
                )}
              </div>
              <div className="text-right">
                {!e.editing ? (
                  <>
                    <button
                      onClick={() =>
                        setEdits((prev) => ({
                          ...prev,
                          [item._id]: {
  price: item.price ?? "",

  originalPrice:
    item.originalPrice ?? "",

  durationInDays:
    item.durationInDays ?? "",

  startDate:
    formatDateInput(item.startDate),

  endDate:
    formatDateInput(item.endDate),

  editing: true,
},
                        }))
                      }
                      className="mr-2 text-sm px-3 py-1 bg-yellow-400 rounded hover:bg-yellow-500"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item._id)}
                      className="text-sm px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleSaveEdit(item._id)}
                      className="mr-2 text-sm px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => cancelEditing(item._id, item)}
                      className="text-sm px-3 py-1 bg-gray-300 rounded hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StudyPlanPricing;
